import {
  ArrowSquareOut,
  CheckCircle,
  Eye,
  FingerprintSimple,
  ShieldCheck,
} from "@phosphor-icons/react";
import { useEffect, useState } from "react";
import { useTranslations } from "use-intl";
import { useParams } from "react-router-dom";
import type { FlowTracePublicDisclosure } from "../../packages/contracts/src/index";
import { useAppLocale } from "../i18n";
import { getPublicDisclosure } from "../lib/api";
import { Brand } from "./Brand";
import { LanguageSwitcher } from "./LanguageSwitcher";
import { SiteFooter } from "./SiteFooter";

type LoadState =
  | { kind: "loading" }
  | { kind: "loaded"; disclosure: FlowTracePublicDisclosure }
  | { kind: "error" };

export const DisclosurePage = () => {
  const t = useTranslations("disclosure");
  const { locale } = useAppLocale();
  const { appId = "" } = useParams();
  const [state, setState] = useState<LoadState>({ kind: "loading" });

  useEffect(() => {
    let active = true;
    setState({ kind: "loading" });
    void getPublicDisclosure(appId)
      .then((disclosure) => {
        if (active) setState({ disclosure, kind: "loaded" });
      })
      .catch(() => {
        if (active) setState({ kind: "error" });
      });
    return () => {
      active = false;
    };
  }, [appId]);

  if (state.kind !== "loaded") {
    return (
      <div className="disclosure-status-page">
        {state.kind === "loading" ? (
          <main className="full-page-status disclosure-status">{t("loading")}</main>
        ) : (
          <main className="disclosure-error">
            <Brand />
            <p className="eyebrow">FLOWTRACE DISCLOSURE</p>
            <h1>{t("notFoundTitle")}</h1>
            <p>{t("notFoundDescription")}</p>
          </main>
        )}
        <SiteFooter />
      </div>
    );
  }

  const { disclosure } = state;
  const formattedDate = new Intl.DateTimeFormat(locale, {
    dateStyle: "medium",
  }).format(new Date(disclosure.updatedAt));
  const collectionItems = [
    t("collection.pageTransitions"),
    t("collection.clicks"),
    t("collection.scroll"),
    t("collection.domChanges"),
    t("collection.browserErrors"),
  ];
  const protectedItems = [
    t("protected.inputValues"),
    t("protected.cookies"),
    t("protected.authorization"),
    t("protected.networkBodies"),
  ];

  return (
    <div className="disclosure-page">
      <header className="disclosure-header">
        <Brand />
        <LanguageSwitcher />
      </header>

      <main>
        <section className="disclosure-hero">
          <div className="disclosure-hero-copy">
            <p className="eyebrow">FLOWTRACE · PUBLIC DISCLOSURE</p>
            <div className="disclosure-trust-chip">
              <CheckCircle aria-hidden="true" size={17} weight="fill" />
              {t("registeredProject")}
            </div>
            <h1>{t("title", { project: disclosure.name })}</h1>
            <p className="disclosure-lead">{t("lead")}</p>
          </div>

          <aside className="disclosure-project-card">
            <span>{t("projectLabel")}</span>
            <strong>{disclosure.name}</strong>
            <code>{disclosure.appId}</code>
            <dl>
              <div>
                <dt>{t("providerLabel")}</dt>
                <dd>{disclosure.provider}</dd>
              </div>
              <div>
                <dt>{t("updatedLabel")}</dt>
                <dd>{formattedDate}</dd>
              </div>
            </dl>
          </aside>
        </section>

        <section className="disclosure-origins" aria-labelledby="disclosure-origins-title">
          <div>
            <p className="eyebrow">{t("sitesEyebrow")}</p>
            <h2 id="disclosure-origins-title">{t("sitesTitle")}</h2>
          </div>
          <div className="disclosure-origin-list">
            {disclosure.origins.map((origin) => (
              <a href={origin} key={origin} rel="noreferrer" target="_blank">
                {new URL(origin).hostname}
                <ArrowSquareOut aria-hidden="true" size={16} />
              </a>
            ))}
          </div>
        </section>

        <section className="disclosure-grid" aria-label={t("detailsLabel")}>
          <article className="disclosure-info-card disclosure-info-card--observed">
            <span className="disclosure-card-icon"><Eye aria-hidden="true" size={22} /></span>
            <p className="eyebrow">{t("observedEyebrow")}</p>
            <h2>{t("observedTitle")}</h2>
            <ul>
              {collectionItems.map((item) => <li key={item}>{item}</li>)}
            </ul>
            <p className="disclosure-note">{t("visibleTextNote")}</p>
          </article>

          <article className="disclosure-info-card disclosure-info-card--protected">
            <span className="disclosure-card-icon"><ShieldCheck aria-hidden="true" size={22} /></span>
            <p className="eyebrow">{t("protectedEyebrow")}</p>
            <h2>{t("protectedTitle")}</h2>
            <ul>
              {protectedItems.map((item) => <li key={item}>{item}</li>)}
            </ul>
          </article>

          <article className="disclosure-info-card disclosure-info-card--choice">
            <span className="disclosure-card-icon"><FingerprintSimple aria-hidden="true" size={22} /></span>
            <p className="eyebrow">{t("choiceEyebrow")}</p>
            <h2>{t("choiceTitle")}</h2>
            <p>{t("choiceDescription")}</p>
            <p className="disclosure-note">{t("settingsNote")}</p>
          </article>
        </section>

        <section className="disclosure-principle">
          <div>
            <p className="eyebrow">{t("principleEyebrow")}</p>
            <h2>{t("principleTitle")}</h2>
          </div>
          <p>{t("principleDescription")}</p>
        </section>
      </main>

      <SiteFooter note={t("footer")} />
    </div>
  );
};
